/**
 * Device Management Page
 *
 * Displays and manages ESP32 devices for workflow deployment.
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { KasaDashboardPage } from '@/integrations/kasa';
import { UnifiProtectDashboardPage } from '@/integrations/unifi-protect';
import { useDeviceStore, Device } from '@/stores/devices/device-store';
import { useAuthStore } from '@/stores/auth/auth-store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Cpu,
  Wifi,
  WifiOff,
  Monitor,
  Hand,
  Plus,
  MoreVertical,
  Trash2,
  Edit,
  Play,
  Square,
  RefreshCw,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const statusColors: Record<string, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-muted-foreground/50',
  deploying: 'bg-primary animate-pulse',
  error: 'bg-destructive',
};

const statusLabels: Record<string, string> = {
  online: 'Online',
  offline: 'Offline',
  deploying: 'Deploying...',
  error: 'Error',
};

type DeviceSubview = 'controllers' | 'connect' | 'cameras';

function isDeviceSubview(value: string | undefined): value is Exclude<DeviceSubview, 'controllers'> {
  return value === 'connect' || value === 'cameras';
}

interface DeviceCardProps {
  device: Device;
  onEdit: (device: Device) => void;
  onDelete: (deviceId: string) => void;
  onDeploy: (device: Device) => void;
  onStop: (deviceId: string) => void;
}

function DeviceCard({ device, onEdit, onDelete, onDeploy, onStop }: DeviceCardProps) {
  const lastSeen = new Date(device.lastSeen);
  const isStale = Date.now() - lastSeen.getTime() > 5 * 60 * 1000;

  return (
    <Card className="relative">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span
              className={`w-3 h-3 rounded-full ${statusColors[device.status]}`}
              title={statusLabels[device.status]}
            />
            <CardTitle className="text-lg">{device.name}</CardTitle>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(device)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              {device.status === 'online' && !device.currentWorkflowId && (
                <DropdownMenuItem onClick={() => onDeploy(device)}>
                  <Play className="h-4 w-4 mr-2" />
                  Deploy Workflow
                </DropdownMenuItem>
              )}
              {device.currentWorkflowId && (
                <DropdownMenuItem onClick={() => onStop(device.id)}>
                  <Square className="h-4 w-4 mr-2" />
                  Stop Workflow
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDelete(device.id)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CardDescription className="flex items-center gap-1 text-xs">
          <Cpu className="h-3 w-3" />
          {device.deviceId}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Capabilities */}
        <div className="flex flex-wrap gap-1">
          {device.capabilities.has_display && (
            <Badge variant="secondary" className="text-xs">
              <Monitor className="h-3 w-3 mr-1" />
              Display
            </Badge>
          )}
          {device.capabilities.has_touch && (
            <Badge variant="secondary" className="text-xs">
              <Hand className="h-3 w-3 mr-1" />
              Touch
            </Badge>
          )}
          {device.capabilities.has_wifi && (
            <Badge variant="secondary" className="text-xs">
              <Wifi className="h-3 w-3 mr-1" />
              WiFi
            </Badge>
          )}
          {device.capabilities.gpio_count > 0 && (
            <Badge variant="secondary" className="text-xs">
              GPIO: {device.capabilities.gpio_count}
            </Badge>
          )}
        </div>

        {/* Current Workflow */}
        {device.currentWorkflow && (
          <div className="text-sm">
            <span className="text-muted-foreground">Running: </span>
            <span className="font-medium">{device.currentWorkflow.name}</span>
          </div>
        )}

        {/* Last Seen */}
        <div className="text-xs text-muted-foreground">
          Last seen: {isStale ? (
            <span className="text-orange-500">{lastSeen.toLocaleString()}</span>
          ) : (
            lastSeen.toLocaleString()
          )}
        </div>

        {/* Firmware Version */}
        {device.firmwareVersion && (
          <div className="text-xs text-muted-foreground">
            Firmware: {device.firmwareVersion}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface AddDeviceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: Partial<Device>) => Promise<void>;
  isLoading: boolean;
}

function AddDeviceDialog({ open, onOpenChange, onSubmit, isLoading }: AddDeviceDialogProps) {
  const [formData, setFormData] = useState({
    deviceId: '',
    name: '',
    has_display: true,
    has_touch: true,
    gpio_count: 20,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      deviceId: formData.deviceId,
      name: formData.name,
      type: 'esp32',
      capabilities: {
        has_display: formData.has_display,
        has_touch: formData.has_touch,
        gpio_count: formData.gpio_count,
        has_wifi: true,
      },
    });
    setFormData({ deviceId: '', name: '', has_display: true, has_touch: true, gpio_count: 20 });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Register New Device</DialogTitle>
          <DialogDescription>
            Add a new ESP32 device to your account. The device ID is typically the MAC address.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deviceId">Device ID (MAC Address)</Label>
            <Input
              id="deviceId"
              placeholder="AA:BB:CC:DD:EE:FF"
              value={formData.deviceId}
              onChange={(e) => setFormData({ ...formData, deviceId: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Device Name</Label>
            <Input
              id="name"
              placeholder="Farm Controller 1"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_display"
                checked={formData.has_display}
                onChange={(e) => setFormData({ ...formData, has_display: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="has_display">Has Display</Label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_touch"
                checked={formData.has_touch}
                onChange={(e) => setFormData({ ...formData, has_touch: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="has_touch">Has Touch</Label>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="gpio_count">GPIO Count</Label>
            <Input
              id="gpio_count"
              type="number"
              min="0"
              max="40"
              value={formData.gpio_count}
              onChange={(e) => setFormData({ ...formData, gpio_count: parseInt(e.target.value, 10) || 0 })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Register Device
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeviceInventoryView() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const {
    devices,
    counts,
    isLoading,
    isInitialized,
    error,
    isSocketConnected,
    initialize,
    fetchDevices,
    registerDevice,
    deleteDevice,
    stopWorkflow,
  } = useDeviceStore();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isInitialized) {
      initialize();
    }
  }, [isAuthenticated, isInitialized, initialize, navigate]);

  const handleAddDevice = async (data: Partial<Device>) => {
    setIsRegistering(true);
    try {
      await registerDevice(data);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleEditDevice = (device: Device) => {
    // TODO: Implement edit dialog
    void device;
  };

  const handleDeleteDevice = async (deviceId: string) => {
    if (confirm('Are you sure you want to delete this device?')) {
      await deleteDevice(deviceId);
    }
  };

  const handleDeploy = (device: Device) => {
    // Navigate to workflow editor with device context
    navigate(`/workflow-editor?target=esp32&deviceId=${device.id}`);
  };

  const handleStopWorkflow = async (deviceId: string) => {
    await stopWorkflow(deviceId);
  };

  const handleRefresh = () => {
    fetchDevices();
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Controllers</h2>
          <p className="text-muted-foreground">
            Manage ESP32 controllers and deploy workflows
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isSocketConnected ? 'default' : 'secondary'}>
            {isSocketConnected ? (
              <><Wifi className="h-3 w-3 mr-1" /> Connected</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" /> Disconnected</>
            )}
          </Badge>
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Device
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold">{counts.total}</div>
            <div className="text-sm text-muted-foreground">Total Devices</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-600">{counts.online}</div>
            <div className="text-sm text-muted-foreground">Online</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-muted-foreground">{counts.offline}</div>
            <div className="text-sm text-muted-foreground">Offline</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-primary">{counts.deploying}</div>
            <div className="text-sm text-muted-foreground">Deploying</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-destructive">{counts.error}</div>
            <div className="text-sm text-muted-foreground">Error</div>
          </CardContent>
        </Card>
      </div>

      {/* Error */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-4 flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            {error}
          </CardContent>
        </Card>
      )}

      {/* Device Grid */}
      {isLoading && !isInitialized ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : devices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Cpu className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No devices registered</h3>
            <p className="text-muted-foreground mb-4">
              Get started by registering your first ESP32 device
            </p>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Device
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              onEdit={handleEditDevice}
              onDelete={handleDeleteDevice}
              onDeploy={handleDeploy}
              onStop={handleStopWorkflow}
            />
          ))}
        </div>
      )}

      {/* Add Device Dialog */}
      <AddDeviceDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSubmit={handleAddDevice}
        isLoading={isRegistering}
      />
    </div>
  );
}

export function DeviceManagementPage() {
  const navigate = useNavigate();
  const { deviceSubview } = useParams<{ deviceSubview?: string }>();

  if (deviceSubview === 'smart') {
    return <Navigate to="/devices/connect" replace />;
  }

  if (deviceSubview && !isDeviceSubview(deviceSubview)) {
    return <Navigate to="/devices" replace />;
  }

  const activeSubview: DeviceSubview = isDeviceSubview(deviceSubview)
    ? deviceSubview
    : 'controllers';

  const handleSubviewChange = (value: string) => {
    if (value === 'controllers') {
      navigate('/devices');
      return;
    }

    if (isDeviceSubview(value)) {
      navigate(`/devices/${value}`);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Devices</h1>
        <p className="text-muted-foreground">
          Manage controllers, connected devices, and cameras
        </p>
      </div>

      <Tabs value={activeSubview} onValueChange={handleSubviewChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 sm:w-auto">
          <TabsTrigger value="controllers">Controllers</TabsTrigger>
          <TabsTrigger value="connect">Connect</TabsTrigger>
          <TabsTrigger value="cameras">Cameras</TabsTrigger>
        </TabsList>

        <TabsContent value="controllers" className="mt-0">
          <DeviceInventoryView />
        </TabsContent>
        <TabsContent value="connect" className="mt-0">
          {activeSubview === 'connect' && <KasaDashboardPage embedded />}
        </TabsContent>
        <TabsContent value="cameras" className="mt-0">
          {activeSubview === 'cameras' && <UnifiProtectDashboardPage embedded />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default DeviceManagementPage;
